/* Daily Horoscope — 12 rashi cards, click for today's reading + subscribe. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Sparkles, Loader2 } from "lucide-react";
import { api, formatApiErr } from "@/lib/api";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function DailyHoroscope() {
    const [rashis, setRashis] = useState([]);
    const [active, setActive] = useState(null);
    const [reading, setReading] = useState("");
    const [loading, setLoading] = useState(false);
    const [subEmail, setSubEmail] = useState("");
    const [subRashi, setSubRashi] = useState("");
    const [subLoading, setSubLoading] = useState(false);

    useEffect(() => {
        api.get("/daily-horoscope/rashis").then((r) => setRashis(r.data || []));
    }, []);

    const openRashi = async (slug) => {
        setActive(slug);
        setReading("");
        setLoading(true);
        try {
            const { data } = await api.get(`/daily-horoscope/today/${slug}`);
            setReading(data.text || "");
        } catch (err) {
            toast.error(formatApiErr(err));
        } finally {
            setLoading(false);
        }
    };

    const subscribe = async (e) => {
        e.preventDefault();
        if (!subRashi) return toast.error("Please select your rashi");
        setSubLoading(true);
        try {
            await api.post("/daily-horoscope/subscribe", { email: subEmail, rashi: subRashi });
            toast.success("Subscribed! Your daily horoscope will start arriving at 6 AM IST.");
            setSubEmail("");
            setSubRashi("");
        } catch (err) {
            toast.error(formatApiErr(err));
        } finally {
            setSubLoading(false);
        }
    };

    const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    const activeRashi = rashis.find((r) => r.slug === active);

    return (
        <div className="pt-32 pb-32">
            <SEO
                title="Daily Horoscope — All 12 Rashis (Vedic)"
                description="Free daily Vedic horoscope for all 12 rashis — love, career, finance & remedies by Dr. Tannu Aggarwal. Get personalized predictions delivered to your inbox every morning."
                path="/daily-horoscope"
                schema={{
                    "@context": "https://schema.org",
                    "@type": "WebPage",
                    "name": "Daily Horoscope · VedicByTannu",
                    "description": "Daily Vedic horoscope for all 12 moon signs (rashis).",
                }}
            />

            <div className="container-vv">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <span className="gold-line" />
                        <span className="label-eyebrow">{today}</span>
                        <span className="gold-line" />
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream leading-tight text-balance">
                        Today's <span className="italic bg-clip-text text-transparent bg-gold-gradient">Vedic Horoscope</span>
                    </h1>
                    <p className="mt-6 text-cream-muted leading-relaxed">
                        Click your rashi (moon sign) for today's reading — covering love, career, finance, and a practical Vedic tip.
                        Generated daily by Dr. Tannu's Vedic AI.
                    </p>
                </div>

                {/* Rashi grid */}
                <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                    {rashis.map((r) => (
                        <button
                            key={r.slug}
                            onClick={() => openRashi(r.slug)}
                            data-testid={`rashi-${r.slug}`}
                            className={`rounded-2xl border p-5 text-left transition-all ${
                                active === r.slug
                                    ? "border-gold bg-gold/10 shadow-lg"
                                    : "border-gold/20 bg-ink-deep/50 hover:border-gold/50 hover:bg-ink-deep"
                            }`}
                        >
                            <div className="text-3xl text-gold">{r.symbol}</div>
                            <div className="mt-2 font-serif text-cream text-lg leading-tight">{r.name_en}</div>
                            <div className="text-xs text-cream-muted">{r.name_hi}</div>
                        </button>
                    ))}
                </div>

                {/* Active reading */}
                {active && (
                    <div id="reading" className="mt-12 rounded-3xl border border-gold/30 bg-ink-deep p-8 max-w-3xl mx-auto" data-testid="horoscope-reading">
                        <div className="flex items-center gap-3 mb-4">
                            <span className="text-3xl text-gold">{activeRashi?.symbol}</span>
                            <div>
                                <div className="font-serif text-2xl text-cream">{activeRashi?.name_en} <span className="text-cream-muted">· {activeRashi?.name_hi}</span></div>
                                <div className="text-xs text-cream-muted">{today}</div>
                            </div>
                        </div>
                        {loading ? (
                            <div className="flex items-center gap-2 text-cream-muted py-8">
                                <Loader2 className="h-5 w-5 animate-spin text-gold" /> Generating your horoscope...
                            </div>
                        ) : (
                            <p className="text-cream-muted leading-relaxed whitespace-pre-wrap" data-testid="horoscope-text">{reading}</p>
                        )}
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/booking" className="btn-gold text-sm" data-testid="horoscope-book-cta">
                                Get a personalized reading <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link to="/free-kundli" className="btn-outline-gold text-sm">
                                Free Kundli <Sparkles className="h-4 w-4" />
                            </Link>
                        </div>
                    </div>
                )}

                {/* Subscribe */}
                <div className="mt-20 rounded-3xl border border-gold/30 bg-gradient-to-br from-ink-deep to-ink p-8 sm:p-12 max-w-3xl mx-auto text-center" data-testid="horoscope-subscribe">
                    <div className="flex justify-center mb-4">
                        <div className="h-14 w-14 rounded-full bg-gold/15 border border-gold/40 grid place-items-center">
                            <Mail className="h-7 w-7 text-gold" />
                        </div>
                    </div>
                    <h2 className="font-serif text-3xl text-cream">Daily horoscope to your inbox</h2>
                    <p className="mt-3 text-cream-muted">Every morning at 6 AM IST. Pick your rashi. Unsubscribe anytime.</p>
                    <form onSubmit={subscribe} className="mt-6 grid sm:grid-cols-3 gap-3 max-w-xl mx-auto">
                        <input
                            required type="email" placeholder="your@email.com"
                            value={subEmail} onChange={(e) => setSubEmail(e.target.value)}
                            className="input-luxe sm:col-span-2"
                            data-testid="horoscope-sub-email"
                        />
                        <select required value={subRashi} onChange={(e) => setSubRashi(e.target.value)} className="input-luxe" data-testid="horoscope-sub-rashi">
                            <option value="">Your Rashi</option>
                            {rashis.map((r) => <option key={r.slug} value={r.slug}>{r.name_en} · {r.name_hi}</option>)}
                        </select>
                        <button type="submit" disabled={subLoading} className="btn-gold sm:col-span-3 disabled:opacity-50" data-testid="horoscope-sub-submit">
                            {subLoading ? "Subscribing..." : "Subscribe — It's Free"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
